import { t } from 'i18next';
import { isUndefined } from 'lodash-es';
import { ComponentProps, ElementType, Suspense } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { CommonLoader } from '~components/Loaders/CommonLoader';
import { SystemRoles } from '~constants/enums';
import { FeatureFlags } from '~constants/featureFlags';
import { RoleItem } from '~hooks/useUsers/models';
import { ROUTES } from '~router/routePaths';
import { hasActiveSession } from '~services/sessionTokens';
import { useStores } from '~store';
import { useAwaitableFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { buildSignInPathWithRedirect } from '~utils/signInRedirect';

interface ProtectedRouteProps extends ComponentProps<ElementType> {
  canAccess?: boolean;
  element: ElementType;
  featureFlag?: FeatureFlags;
  requiredRoles?: SystemRoles[];
  returnAfterSignIn?: boolean;
}

export const ProtectedRoute = ({
  canAccess,
  element: Component,
  featureFlag,
  requiredRoles,
  returnAfterSignIn,
  ...rest
}: ProtectedRouteProps) => {
  const location = useLocation();
  const { userStore } = useStores();
  const isAuthenticated = hasActiveSession();

  const routeFeatureFlag = useAwaitableFeatureFlag({
    featureFlagKey: featureFlag ?? ('___DUMMY_FEATURE_FLAG___' as FeatureFlags),
  });

  let isAllowed = true;

  if (requiredRoles?.length) {
    isAllowed = !!userStore.user.userRoles?.some(
      (role: RoleItem) => role.name === SystemRoles.PLATFORM_ADMIN,
    );
  }

  if (!isUndefined(canAccess)) {
    isAllowed = canAccess;
  }

  if (featureFlag && !routeFeatureFlag.isFeatureFlagEnabled) {
    isAllowed = false;
  }

  // Split reports the unresolved 'control' treatment until it is ready, which never
  // satisfies the 'on' check, so redirecting on that first render bounces a reload
  // before the flag can resolve.
  const isWaitingOnFeatureFlag =
    isAuthenticated && !!featureFlag && routeFeatureFlag.isAwaitingTreatment;

  if (isWaitingOnFeatureFlag) {
    return <CommonLoader />;
  }

  const signInPath = returnAfterSignIn
    ? buildSignInPathWithRedirect(
        `/${ROUTES.signIn}`,
        `${location.pathname}${location.search}${location.hash}`,
      )
    : `/${ROUTES.signIn}`;

  return (
    <Suspense fallback={<div>{t('common.loading')}</div>}>
      {isAuthenticated ? (
        isAllowed ? (
          <Component {...rest} />
        ) : (
          <Navigate to="/" />
        )
      ) : (
        <Navigate to={signInPath} />
      )}
    </Suspense>
  );
};
